import React from 'react';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Separator } from '../../../ui/separator';
import { Plus, X } from 'lucide-react';
import { RATE_TYPES } from '../../constants';

/**
 * Single-product mode section.
 *
 * Rendered when neither ``isPlywoodBulkMode`` nor ``isWeightBulkCategory``
 * apply — i.e. for the "one product at a time" categories: Veneers,
 * Hardware, Screws/Patti, Laminates, Highlighters, ACP, Wooden Flooring,
 * plus any custom category the admin creates.
 *
 * Contains three logical blocks:
 *  1. Thickness / Size / Finish (adhesives simple form gets no thickness).
 *  2. Base rate + rate type, with size-unit / pieces-per-box helpers.
 *  3. Optional secondary rate (Wooden Flooring + Adhesives simple form),
 *     Wooden Flooring "sqft per box" input, and the Laminates/Highlighters/
 *     ACP/Wooden Flooring "Codes & Rates" repeater.
 */
export function SingleProductSection({ form, setForm }) {
  const isAdhesive = form.main_category === 'Adhesives';
  const isThinFinishCategory = ['Laminates', 'Highlighters'].includes(form.main_category);
  const showSecondaryRate = ['Wooden Flooring', 'Adhesives'].includes(form.main_category);
  const showCodesAndRates = ['Laminates', 'Highlighters', 'ACP', 'Wooden Flooring'].includes(form.main_category);

  return (
    <>
      {/* Hide Thickness for Adhesives, show with different placeholder for
          Laminates/Highlighters (they measure in <1MM finishes). */}
      {isAdhesive ? (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Size</Label><Input value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} placeholder="e.g., 8x4" className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-size" /></div>
          <div><Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Finish</Label><Input value={form.finish} onChange={e => setForm(f => ({ ...f, finish: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-finish" /></div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Thickness</Label>
            <Input
              value={form.thickness}
              onChange={e => setForm(f => ({ ...f, thickness: e.target.value }))}
              placeholder={isThinFinishCategory ? 'e.g., 1MM, 0.8MM' : 'e.g., 19MM'}
              className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
              data-testid="form-thickness"
            />
          </div>
          <div><Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Size</Label><Input value={form.size} onChange={e => setForm(f => ({ ...f, size: e.target.value }))} placeholder="e.g., 8x4" className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-size" /></div>
          <div><Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Finish</Label><Input value={form.finish} onChange={e => setForm(f => ({ ...f, finish: e.target.value }))} className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-finish" /></div>
        </div>
      )}
      <Separator />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Base Rate (₹) *</Label>
          <Input type="number" value={form.base_rate} onChange={e => setForm(f => ({ ...f, base_rate: parseFloat(e.target.value) || 0 }))} className="rounded-xl mt-1 text-base md:text-sm tabular-nums bg-heda-cream/50" data-testid="form-base-rate" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Rate Type</Label>
          <Select value={form.rate_type} onValueChange={v => setForm(f => ({ ...f, rate_type: v }))}>
            <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="form-rate-type"><SelectValue /></SelectTrigger>
            <SelectContent>{RATE_TYPES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {form.rate_type === 'per sq ft' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Size Unit <span className="text-heda-mute">(for sq ft calculation)</span></Label>
            <Select value={form.size_unit || 'feet'} onValueChange={v => setForm(f => ({ ...f, size_unit: v }))}>
              <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="form-size-unit-single"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feet">Feet</SelectItem>
                <SelectItem value="inches">Inches</SelectItem>
                <SelectItem value="mm">Millimeters (mm)</SelectItem>
                <SelectItem value="meters">Meters</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      {form.rate_type === 'per box' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Pieces per Box <span className="text-heda-mute">(quantity in one box)</span></Label>
            <Input
              type="number"
              placeholder="e.g., 100"
              value={form.pieces_per_box || ''}
              onChange={e => setForm(f => ({ ...f, pieces_per_box: parseInt(e.target.value, 10) || 0 }))}
              className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50"
              data-testid="form-pieces-per-box-single"
            />
          </div>
        </div>
      )}

      {/* SECONDARY RATE — Wooden Flooring (and Adhesives if you want to
          add a second rate to the simple form too). Optional: if a value
          is set, the cart shows a "Bill by [Primary] | [Secondary]" chip
          so the salesperson can pick on a per-line basis. Mirrors the
          nails/screws pattern that staff already know. Leave both at 0
          to hide the toggle entirely. */}
      {showSecondaryRate && (
        <div className="border border-heda-gold/30 rounded-xl p-3 bg-amber-50">
          <p className="text-xs font-semibold text-amber-700 mb-2">Secondary Rate <span className="text-heda-mute font-normal">(optional — leave blank to hide)</span></p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Secondary Rate (₹)</Label>
              <Input
                type="number"
                value={form.secondary_base_rate || ''}
                onChange={e => setForm(f => ({ ...f, secondary_base_rate: parseFloat(e.target.value) || 0 }))}
                placeholder="e.g., 2400"
                className="rounded-xl mt-1 text-base md:text-sm tabular-nums bg-heda-cream/50"
                data-testid="form-secondary-base-rate"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Secondary Rate Type</Label>
              <Select
                value={form.secondary_rate_type || (form.main_category === 'Wooden Flooring' ? 'per box' : 'per bundle')}
                onValueChange={v => setForm(f => ({ ...f, secondary_rate_type: v }))}
              >
                <SelectTrigger className="rounded-xl mt-1 text-sm bg-heda-cream/50" data-testid="form-secondary-rate-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per box">per box</SelectItem>
                  <SelectItem value="per sq ft">per sq ft</SelectItem>
                  <SelectItem value="per bundle">per bundle</SelectItem>
                  <SelectItem value="per piece">per piece</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Wooden Flooring only: how many sq ft are in one box. Lets the
              cart accept quantity in EITHER boxes or sq ft and convert
              (rounding up to whole boxes — we sell sealed boxes only,
              never less than requested). */}
          {form.main_category === 'Wooden Flooring' && (
            <div className="mt-3 pt-3 border-t border-heda-gold/30">
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Sq Ft per Box</Label>
              <Input
                type="number"
                step="0.01"
                value={form.sqft_per_box || ''}
                onChange={e => setForm(f => ({ ...f, sqft_per_box: parseFloat(e.target.value) || 0 }))}
                placeholder="e.g., 22.5"
                className="rounded-xl mt-1 text-base md:text-sm tabular-nums max-w-[200px] bg-heda-cream/50"
                data-testid="form-sqft-per-box"
              />
              <p className="text-[11px] text-heda-mute mt-1">
                Used to convert customer&apos;s sq-ft requirement → whole boxes (rounded up). Shown in the PDF as <em>&ldquo;1 box = X sq ft&rdquo;</em>.
              </p>
            </div>
          )}
        </div>
      )}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Image URL</Label>
        <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="rounded-xl mt-1 text-base md:text-sm bg-heda-cream/50" data-testid="form-image-url" />
      </div>

      {/* CODES AND RATES for Laminates, Highlighters, ACP and Wooden Flooring
          (all share the "one brand series → many codes" data shape). */}
      {showCodesAndRates && <CodesAndRates form={form} setForm={setForm} />}
    </>
  );
}


/**
 * "Codes & Rates" repeater used by Laminates / Highlighters / ACP /
 * Wooden Flooring — each brand series has many product codes, each with
 * its own rate and available sizes.
 */
function CodesAndRates({ form, setForm }) {
  return (
    <div className="border border-heda-line rounded-xl p-3 bg-heda-cream/50">
      {/* Rate Type Selection */}
      <div className="flex items-center gap-4 mb-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Rate Type:</Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, rate_type: 'per sq ft' }))}
            className={`px-3 py-1 text-xs rounded-full border transition-all ${
              form.rate_type === 'per sq ft'
                ? 'bg-heda-ink text-white border-heda-ink'
                : 'border-heda-line hover:border-heda-gold'
            }`}
          >
            Per Sq Ft
          </button>
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, rate_type: 'per sheet' }))}
            className={`px-3 py-1 text-xs rounded-full border transition-all ${
              form.rate_type === 'per sheet'
                ? 'bg-heda-ink text-white border-heda-ink'
                : 'border-heda-line hover:border-heda-gold'
            }`}
          >
            Per Sheet
          </button>
        </div>
      </div>

      <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute flex items-center justify-between">
        <span>Codes &amp; Rates ({form.codes_and_rates?.length || 0})</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-6 text-xs"
          onClick={() => {
            // Auto-fill sizes from first code if exists
            const firstCodeSizes = form.codes_and_rates?.[0]?.sizes || [];
            const newCode = { code: '', rate: 0, sizes: [...firstCodeSizes] };
            setForm(f => ({ ...f, codes_and_rates: [...(f.codes_and_rates || []), newCode] }));
          }}
        >
          <Plus className="w-3 h-3 mr-1" /> Add Code
        </Button>
      </Label>
      <p className="text-[11px] text-heda-mute mt-1">Tip: Sizes auto-copy from first code. Use comma for multiple (8x4,10x4)</p>

      {form.codes_and_rates && form.codes_and_rates.length > 0 ? (
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
          {form.codes_and_rates.map((cr, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-heda-line">
              <Input
                placeholder="Code"
                value={cr.code || ''}
                onChange={e => {
                  const updated = [...form.codes_and_rates];
                  updated[idx] = { ...updated[idx], code: e.target.value };
                  setForm(f => ({ ...f, codes_and_rates: updated }));
                }}
                className="rounded-xl h-8 text-base md:text-xs flex-1 font-mono bg-heda-cream/50"
              />
              <Input
                type="number"
                placeholder="Rate"
                value={cr.rate || ''}
                onChange={e => {
                  const updated = [...form.codes_and_rates];
                  updated[idx] = { ...updated[idx], rate: parseFloat(e.target.value) || 0 };
                  setForm(f => ({ ...f, codes_and_rates: updated }));
                }}
                className="rounded-xl h-8 text-base md:text-xs w-24 tabular-nums bg-heda-cream/50"
              />
              <Input
                placeholder="Sizes (8x4,10x4)"
                value={(cr.sizes || []).join(',')}
                onChange={e => {
                  const updated = [...form.codes_and_rates];
                  updated[idx] = { ...updated[idx], sizes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) };
                  setForm(f => ({ ...f, codes_and_rates: updated }));
                }}
                className="rounded-xl h-8 text-base md:text-xs w-28 bg-heda-cream/50"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full hover:bg-heda-cream h-7 w-7 p-0 text-red-700 hover:bg-red-50"
                onClick={() => {
                  const updated = form.codes_and_rates.filter((_, i) => i !== idx);
                  setForm(f => ({ ...f, codes_and_rates: updated }));
                }}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-heda-mute mt-2">No codes added. Click &quot;Add Code&quot; to add product codes.</p>
      )}
    </div>
  );
}
